import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { List, ListItemButton, ListItemText, ListItemIcon, IconButton, Box, Tooltip, Menu, MenuItem, Divider } from '@mui/material';
import { Context } from '../../app/Context';
import type { Sidebar as Interface } from '../../design/components';
import type { Project } from '../../design/types';
import { FontIcon } from './FontIcon';
import { AdaIcon } from '../shared/AdaIcon';

export const Sidebar: React.FC<Interface> = ({ state, onAssistantToggle, assistantOpen }) => {
  const [context, setContext] = useContext(Context)!
  const models = [...new Map(context.authn
    .filter(auth => auth.project?.id === context.project?.id)
    .map(auth => auth.capability.model)
    .map(model => [model.id, model])).values()]
    .filter(model => !model.hidden && model.type !== 'personal')
    .sort((a, b) => a.path === '/' ? -1 : b.path === '/' ? 1 : 0);
  const [collapsed, setCollapsed] = state || [false, () => {}];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Derive unique projects from all authorizations
  const authorizedProjects = React.useMemo(() => {
    const projectMap = new Map();
    context.authn.forEach(a => {
      if (a.project?.id) projectMap.set(a.project.id, a.project);
    });
    return Array.from(projectMap.values());
  }, [context.authn]);

  const handleProjectClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProjectClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSelect = (project: Project) => {
    setContext(prev => ({ ...prev, project }));
    handleProjectClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <List component="nav">
        {/* Project switcher */}
        {collapsed ? (
          <Tooltip title={context.project?.name ?? 'Select project'} placement="right">
            <ListItemButton onClick={handleProjectClick} sx={{ justifyContent: 'center' }}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <FontIcon name="Folder" />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton onClick={handleProjectClick}>
            <ListItemIcon>
              <FontIcon name="Folder" />
            </ListItemIcon>
            <ListItemText primary={context.project?.name ?? 'Select project'} />
            <FontIcon name="UnfoldMore" />
          </ListItemButton>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProjectClose}
        >
          {authorizedProjects.map((proj) => (
            <MenuItem
              key={proj.id}
              selected={proj.id === context.project?.id}
              onClick={() => handleProjectSelect(proj)}
            >
              {proj.name}
            </MenuItem>
          ))}
        </Menu>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', p: 1 }}>
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small" sx={{ cursor: collapsed ? 'e-resize' : 'w-resize' }}>
            <FontIcon name={collapsed ? 'Menu' : 'MenuOpen'} />
          </IconButton>
        </Box>
        {models.map((model) => {
          return collapsed ? (
            <Tooltip key={model.id} title={model.name} placement="right">
              <ListItemButton component={Link} to={model.path} sx={{ justifyContent: 'center' }}>
                {model.icon && (
                  <ListItemIcon sx={{ minWidth: 'auto' }}>
                    <FontIcon name={model.icon} />
                  </ListItemIcon>
                )}
              </ListItemButton>
            </Tooltip>
          ) : (
            <ListItemButton key={model.id} component={Link} to={model.path}>
              {model.icon && (
                <ListItemIcon>
                  <FontIcon name={model.icon} />
                </ListItemIcon>
              )}
              <ListItemText primary={model.name} />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <List component="nav" sx={{ py: 0 }}>
        {collapsed ? (
          <Tooltip title={assistantOpen ? 'Close Ada' : 'Open Ada'} placement="right">
            <ListItemButton onClick={onAssistantToggle} sx={{ justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <AdaIcon size={24} animated={!assistantOpen} />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton onClick={onAssistantToggle} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <ListItemIcon>
              <AdaIcon size={24} animated={!assistantOpen} />
            </ListItemIcon>
            <ListItemText primary="Ada" />
          </ListItemButton>
        )}
        {context.user && (collapsed ? (
          <Tooltip title="Logout" placement="right">
            <ListItemButton onClick={() => context.iam.logout()} sx={{ justifyContent: 'center' }}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <FontIcon name="LogOut" />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton onClick={() => context.iam.logout()}>
            <ListItemIcon>
              <FontIcon name="LogOut" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};
